import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  effect,
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  REQUEST,
  signal,
  TransferState,
} from '@angular/core';
import type { Request } from 'express';
import { firstValueFrom, from, Observable } from 'rxjs';
import { FAQS } from '../../interfaces/faq';
import { THEME_SIGNAL } from '../../interfaces/theme';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';
import { FAQ_CONTENT_TOKEN } from '../../tokens/server-tokens';

const FAQ_TRANSFER_KEY = makeStateKey<FAQS>('FAQ_TRANSFER_KEY');

@Injectable({
  providedIn: 'root',
})
export class FaqService {
  public faqContent = signal<FAQS>({});

  protected subscriberService = inject(SubscriberService);

  protected cacheService = inject(CacheService);

  protected http = inject(HttpClient);

  protected transferState = inject(TransferState);

  protected platform = inject(PLATFORM_ID);

  protected faqContentManager = inject(FAQ_CONTENT_TOKEN, { optional: true });

  protected apiConfig = inject(ApiConfigService);

  protected theme = inject(THEME_SIGNAL);

  protected request: Request | null = inject<Request | null>(REQUEST);

  constructor() {
    if (isPlatformBrowser(this.platform)) {
      this.faqContent.set(this.transferState.get(FAQ_TRANSFER_KEY, {}));
    }

    effect(() => {
      this.subscriberService.subscriberLanguage();

      if (isPlatformServer(this.platform)) {
        return;
      }

      this.fetchFromApi().subscribe({
        next: (value) => {
          this.faqContent.set(value);
        },
        error: (err) => {
          console.error(err);
        },
      });
    });
  }

  public fetchFromApi(): Observable<FAQS> {
    const host = globalThis.location.host;
    const cacheContent = this.cacheService.cachedContent();
    return this.http.get<FAQS>(this.apiConfig.hostApiUrl('faqs'), {
      params: {
        host,
        s3Bucket: cacheContent?.cache.config.webAppConfig.s3BucketAssets || '',
        language: this.subscriberService.subscriberLanguage().code,
        customerId: cacheContent?.cache.customerId || '',
      },
    });
  }

  public directFetchInServer(): Observable<FAQS> {
    if (!this.faqContentManager) {
      throw new Error('FaqContent service for server not initialized');
    }

    const cacheContent = this.cacheService.cachedContent();

    if (!cacheContent) {
      throw new Error('Cache content not initialized');
    }

    const subscriberLanguage = this.subscriberService.subscriberLanguage();

    return from(
      this.faqContentManager.fetchFaqContent(
        cacheContent.cache.config.webAppConfig.s3BucketAssets,
        cacheContent.cache.customerId,
        this.cacheService.getHost(),
        subscriberLanguage.code.toLocaleLowerCase(),
        this.theme()
      )
    );
  }

  /**
   * Initializes for server
   * @returns
   */
  public async initFAQsInServer(): Promise<FAQS> {
    const faqs = await firstValueFrom(this.directFetchInServer());

    this.faqContent.set(faqs);
    this.transferState.set(FAQ_TRANSFER_KEY, faqs);

    return faqs;
  }
}
